import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Hook to detect if the user can navigate back in history
 *
 * Returns true if:
 * - Navigation history exists (user navigated from another page)
 * - location.key !== 'default' (React Router sets 'default' on first visit)
 *
 * Returns false if:
 * - First page visit
 * - User entered URL directly
 * - User refreshed the page
 *
 * This prevents showing a back button when there's nowhere to go back to
 *
 * @returns boolean - True if navigation history exists
 *
 * @example
 * const canGoBack = useCanGoBack()
 *
 * return (
 *   <div>
 *     {canGoBack && <Button onClick={() => navigate(-1)}>Back</Button>}
 *   </div>
 * )
 */
export function useCanGoBack(): boolean {
  const location = useLocation()
  const [canGoBack, setCanGoBack] = useState(false)

  useEffect(() => {
    // location.key === 'default' means first visit (no history)
    // Any other key means user navigated from within the app
    const hasHistory = location.key !== 'default'
    setCanGoBack(hasHistory)
  }, [location])

  return canGoBack
}
